
import {collectDeliveryDate} from '../features/orderDelivery';
import {Environment, Feature} from '../reducers/environmentReducer';
import {Carts, DefaultCart, updateCartCalculation} from '../reducers/cartReducer';
import {PeachPayCustomer, updateCustomerFields} from '../reducers/peachPayCustomerReducer';
import {PeachPayOrder, setOrderError} from '../reducers/peachPayOrderReducer';
import {store} from '../store';
import {getBaseURL} from '../util/environment';
import {MerchantConfiguration} from '../reducers/merchantConfigurationReducer';
import {type ICartCalculationResponse} from '../../../@type/woocommerce/cart-calculation';
import {getLocaleText} from '../util/translation';
import {shippingIsValid} from '../features/shipping';
import {PaymentConfiguration, setSelectedPaymentGateway, updateAvailableGateways, updateGatewayDetails} from '../reducers/paymentConfigurationReducer';
import {type IResponse} from '../../../@type/response';
import {stripHtml, $qsAll, $qs} from '../../../@shared/ts/dom';
import {type WCOrder} from '../../../@type/woocommerce/order';
import {captureSentryException} from '../../../@shared/ts/sentry';
import {getCaptchaToken} from '../features/botProtection';
import {type Maybe, maybeFetch} from '../../../@shared/ts/maybe';
import {getErrorString} from '../../../@shared/ts/error';
import {FeatureFlag} from '../../../@type/features';

export interface OrderService {
	placeOrder: (transaction: Transaction, extraFormData?: Record<string, string>) => Promise<Maybe<WCOrder>>;
	startTransaction: (paymentGateway: string) => Promise<Maybe<Transaction>>;
}

export interface Transaction {
	getId: () => string;
	update: (options: TransactionUpdate) => void;
	complete: (options?: TransactionUpdate) => Promise<boolean>;
}

export function getOrderService(): OrderService {
	return {
		placeOrder,
		async startTransaction(gatewayId: string): Promise<Maybe<Transaction>> {
			const transactionId = await createPaymentTransaction(gatewayId);
			let complete = false;
			if (!transactionId) {
				return {error: new Error('Failed to create a transaction. Please refresh the page and try again.')};
			}

			const transactionUpdates: TransactionUpdate[] = [];

			return {
				result: {
					getId() {
						return transactionId;
					},
					update(options: TransactionUpdate) {
						transactionUpdates.push(options);
					},
					async complete(options?: TransactionUpdate) {
						if (complete) {
							console.error('Developer error: Transaction already completed.');
							return false;
						}

						complete = true;

						if (options) {
							transactionUpdates.push(options);
						}

						if (transactionUpdates.length === 0) {
							return false;
						}

						const update = transactionUpdates.reduce((pt, ct) => ({...pt, ...ct}), {});
						return updatePaymentTransaction(transactionId, update);
					},
				},
			};
		},
	};
}

export interface TransactionUpdate {
	paymentStatus?: string;
	orderStatus?: string;
	/**
     * @deprecated in favor of reporting from PHP side
     */
	order?: WCOrder | null;
	paypal?: {'transaction_id'?: string};
	stripe?: {'charge_id'?: string; 'customer_id'?: string};
	amazonpay?: {'session_id'?: string};
	square?: {'payment_id'?: string};
	purchaseOrder?: {'purchase_order_number'?: string};
	note?: string;
}

async function placeOrder(transaction: Transaction, extraFormData: Record<string, string> = {}): Promise<Maybe<WCOrder>> {
	const checkoutURL = Feature.metadata<string>(FeatureFlag.EXPRESS_CHECKOUT, 'checkout_url');
	const checkoutNonce = Feature.dynamicMetadata<string>(FeatureFlag.EXPRESS_CHECKOUT, 'checkout_nonce');

	if (!checkoutURL || !checkoutNonce) {
		return {error: new Error('Invalid checkout URL or nonce')};
	}

	const formData = new FormData();
	formData.append('woocommerce-process-checkout-nonce', checkoutNonce);

	const gatewayId = PaymentConfiguration.selectedGateway();
	if (gatewayId !== 'peachpay_free') {
		formData.append('payment_method', gatewayId);
	}

	formData.append('peachpay_session_id', PeachPayOrder.sessionId());
	formData.append('peachpay_transaction_id', transaction.getId());
	formData.append('peachpay_captcha_token', await getCaptchaToken());

	formData.append('terms', '1');
	formData.append('european_gdpr', '1');

	formData.append('ct-ultimate-gdpr-consent-field', 'on');
	formData.append('ct-ultimate-gdpr-consent-field-additional', '1');
	formData.append('ct-ultimate-gdpr-consent-field-additional', '1');
	formData.append('ct-ultimate-gdpr-consent-field-label-text', '1');
	formData.append('delivery_date', collectDeliveryDate());

	for (const [key, value] of Object.entries(PeachPayOrder.orderFormRecord())) {
		formData.append(key, value);
	}

	for (const [key, value] of Object.entries(extraFormData)) {
		formData.append(key, value);
	}

	const {error: orderError, result: orderResult} = await maybeFetch<WCOrder>(checkoutURL + '&pp-express-checkout', {
		method: 'POST',
		credentials: 'same-origin',
		body: formData,
	});

	if (orderError || !orderResult || orderResult.result !== 'success') {
		if (orderError) {
			if (orderError instanceof Error) {
				captureSentryException(orderError);
			} else {
				captureSentryException(new Error(getErrorString(orderError)));
			}

			const errorMessage = getErrorString(orderError);
			displayPaymentErrorMessage(errorMessage);

			await transaction.complete({
				note: errorMessage,
			});

			return {error: orderError, result: orderResult};
		}

		if (orderResult?.result === 'failure') {
			const errorMessage = stripHtml(orderResult.messages, null).trim() || getLocaleText('Unknown error occured while placing order. Please refresh the page and try again.');
			displayPaymentErrorMessage(errorMessage);

			await transaction.complete({
				note: errorMessage,
			});

			// In the scenario the order fails but a customer was registered all the existing nonces are
			// now invalid. We need to refresh the checkout to get new ones.
			if (orderResult.refresh || orderResult.reload) {
				await requestCartCalculation();
			}

			return {error: orderError, result: orderResult};
		}

		const errorMessage = getLocaleText('Unknown error occured while placing order. Please refresh the page and try again.');
		displayPaymentErrorMessage(errorMessage);

		await transaction.complete({
			note: errorMessage,
		});

		// Any failure after an order risks invalid nonces in the case of a user being logged in. To prevent
		// possible problems we will get a new cart calculation.
		await requestCartCalculation();
	}

	return {error: orderError, result: orderResult};
}

/**
 * Calls a endpoint in the PeachPay Plugin to recalculate all the summary cost
 */
export async function requestCartCalculation(sync = false): Promise<void> {
	const cartUrl = Feature.metadata<string>(FeatureFlag.EXPRESS_CHECKOUT, 'calculation_url');
	if (!cartUrl) {
		throw new Error('Invalid cart calculation URL');
	}

	const formData = new FormData();
	if (!sync) {
		formData.append('billing_email', PeachPayCustomer.billing.email());
		formData.append('billing_first_name', PeachPayCustomer.billing.firstName());
		formData.append('billing_last_name', PeachPayCustomer.billing.lastName());
		formData.append('billing_phone', PeachPayCustomer.billing.phone());
		formData.append('billing_company', PeachPayCustomer.billing.company());
		formData.append('billing_address_1', PeachPayCustomer.billing.address1());
		formData.append('billing_address_2', PeachPayCustomer.billing.address2());
		formData.append('billing_city', PeachPayCustomer.billing.city());
		formData.append('billing_state', PeachPayCustomer.billing.state());
		formData.append('billing_country', PeachPayCustomer.billing.country());
		formData.append('billing_postcode', PeachPayCustomer.billing.postal());

		if (PeachPayCustomer.shipToDifferentAddress()) {
			formData.append('ship_to_different_address', '1');
			formData.append('shipping_first_name', PeachPayCustomer.shipping.firstName());
			formData.append('shipping_last_name', PeachPayCustomer.shipping.lastName());
			formData.append('shipping_phone', PeachPayCustomer.shipping.phone());
			formData.append('shipping_company', PeachPayCustomer.shipping.company());
			formData.append('shipping_address_1', PeachPayCustomer.shipping.address1());
			formData.append('shipping_address_2', PeachPayCustomer.shipping.address2());
			formData.append('shipping_city', PeachPayCustomer.shipping.city());
			formData.append('shipping_state', PeachPayCustomer.shipping.state());
			formData.append('shipping_country', PeachPayCustomer.shipping.country());
			formData.append('shipping_postcode', PeachPayCustomer.shipping.postal());
		}

		for (const [packageKey, methodKey] of Object.entries(PeachPayOrder.collectSelectedShipping())) {
			formData.append(`shipping_method[${packageKey}]`, methodKey);
		}

		const gatewayId = PaymentConfiguration.selectedGateway();
		if (gatewayId !== 'peachpay_free') {
			formData.append('payment_method', gatewayId);
		}
	}

	const {error: calculationError, result: calculationResult} = await maybeFetch<ICartCalculationResponse>(cartUrl, {
		method: 'POST',
		credentials: 'same-origin',
		body: formData,
	});

	if (calculationError || !calculationResult || !calculationResult.success) {
		if (calculationError) {
			if (calculationError instanceof Error) {
				captureSentryException(calculationError);
			} else {
				captureSentryException(new Error(getErrorString(calculationError)));
			}
		} else if (calculationResult && !calculationResult.success && calculationResult.message) {
			captureSentryException(new Error(calculationResult.message), {
				notices: calculationResult.notices,
			});
		} else {
			captureSentryException(new Error('Unknown error occured while recalculating cart.'));
		}

		return;
	}

	consumeCartCalculationResponse(calculationResult);
}

export function consumeCartCalculationResponse(response: ICartCalculationResponse) {
	if (response.notices) {
		if (response.notices.error) {
			let cartErrors = '';
			for (const errorNotice of response.notices.error) {
				renderOrderNotice(errorNotice.notice);
				cartErrors += errorNotice.notice;
			}

			store.dispatch(setOrderError(cartErrors));
		}

		if (response.notices.success) {
			for (const successNotice of response.notices.success) {
				renderOrderNotice(successNotice.notice);
			}
		}

		if (response.notices.notice) {
			for (const notice of response.notices.notice) {
				renderOrderNotice(notice.notice);
			}
		}
	}

	if (response.data) {
		store.dispatch(setOrderError(''));
		const existingCustomerData = PeachPayCustomer.data().form_fields;
		// On express checkout opening we always want the shipping section collapsed. We remove it from
		// the existing data to accomplish that. If it is not a fresh opening of the checkout then the
		// cart calculation will not include the 'ship_to_different_address' resulting in the section being
		// collapsed.
		// eslint-disable-next-line @typescript-eslint/no-dynamic-delete
		delete existingCustomerData['ship_to_different_address'];
		store.dispatch(updateCustomerFields({
			...existingCustomerData,
			...response.data.customer,
		}));
		store.dispatch(updateCartCalculation(response.data.cart_calculation_record));
		store.dispatch(updateGatewayDetails(response.data.gateway_availability_details));

		if (DefaultCart.contents().length >= 1 && Carts.total() === 0) {
			store.dispatch(updateAvailableGateways(['peachpay_free']));
		} else {
			store.dispatch(updateAvailableGateways(response.data.available_gateway_ids));
		}

		if (DefaultCart.contents().length === 0) {
			store.dispatch(setOrderError(`<span>${getLocaleText('Cart is empty')}</span>`));
		} else if (!shippingIsValid() && Environment.modalUI.page() !== 'info') {
			if (PeachPayCustomer.shipping.country() === '' || PeachPayCustomer.shipping.city() === '' || PeachPayCustomer.shipping.postal() === '') {
				return;
			}

			store.dispatch(setOrderError(`<span>${getLocaleText('There are no shipping methods available')}</span>`));
		}

		const gatewayId = PaymentConfiguration.checkEligibleOrFindAlternate(PaymentConfiguration.selectedGateway());
		if (gatewayId) {
			store.dispatch(setSelectedPaymentGateway(gatewayId));
		} else {
			store.dispatch(setSelectedPaymentGateway(''));
			store.dispatch(setOrderError(`<span>${getLocaleText('There are no payment methods available')}</span>`));
		}
	}
}

export function renderOrderNotice(data: string) {
	const message = stripHtml(data);

	const noticeHTML = `<div class="pp-notice">${message}</div>`;

	$qsAll('.pp-notice-container').forEach(element => {
		element.classList.remove('hide');
		if (window.innerWidth > 900 && element.id === 'pp-notice-container-mobile') {
			element.classList.add('hide');
		}

		setTimeout(() => {
			element.classList.add('hide');
		}, 10050);
	});

	$qs('#pp-notice-container-new')?.insertAdjacentHTML('afterbegin', noticeHTML);
	if (Environment.customer.mobile() || window.innerWidth <= 900) {
		$qs('#pp-notice-container-mobile')?.insertAdjacentHTML('afterbegin', noticeHTML);
	}

	// Remove any 'pp-notice' elements currently in DOM after 10 seconds.
	$qsAll<HTMLElement>('.pp-notice', $el =>
		setTimeout(() => {
			$el?.remove();
		}, 10000));
}

async function createPaymentTransaction(paymentGateway: string): Promise<string | null> {
	try {
		const response = await fetch(getBaseURL(MerchantConfiguration.hostName(), Environment.testMode()) + 'api/v1/transaction/create', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				session: {
					id: PeachPayOrder.sessionId(),
					merchant_id: MerchantConfiguration.id(),
					merchant_name: MerchantConfiguration.name(),
					merchant_url: MerchantConfiguration.hostName(),
					plugin_version: Environment.plugin.version(),
				},
				transaction: {
					payment_method: paymentGateway,
					payment_method_variation: null,
				},
			}),
		});

		const body = await response.json() as IResponse<{transaction_id: string}>;

		if (!response.ok || !body.success) {
			captureSentryException(new Error('Failed to create a new payment transaction'));
			return null;
		}

		return body.data.transaction_id;
	} catch (error) {
		if (error instanceof Error) {
			captureSentryException(new Error(`Unknown error while attempting to create a new payment transaction :: ${error.toString()}`));
		}

		return null;
	}
}

async function updatePaymentTransaction(transactionId: string, options: TransactionUpdate): Promise<boolean> {
	try {
		const requestURL = getBaseURL(MerchantConfiguration.hostName(), Environment.testMode()) + 'api/v1/transaction/update';
		const requestBody = JSON.stringify({
			session: {
				id: PeachPayOrder.sessionId(),
				merchant_id: MerchantConfiguration.id(),
				merchant_name: MerchantConfiguration.name(),
				merchant_url: MerchantConfiguration.hostName(),
				plugin_version: Environment.plugin.version(),
			},
			transaction: {
				id: transactionId,
				paypal: options.paypal,
				stripe: options.stripe,
				square: options.square,
				purchase_order: options.purchaseOrder,
				note: options.note,
			},
			order: {
				payment_status: options.paymentStatus,
				order_status: options.orderStatus,
				data: options.order,
			},
		});

		const response = await fetch(requestURL, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: requestBody,
		});

		const responseBody = await response.json() as IResponse<{transaction_id: string}>;

		if (!response.ok || !responseBody.success) {
			captureSentryException(new Error('Failed to update an existing payment transaction'), {
				transaction_id: transactionId,
			});
			return false;
		}

		return true;
	} catch (error) {
		if (error instanceof Error) {
			captureSentryException(new Error(`Unknown error while attempting to update a existing payment transaction :: ${error.toString()}`), {
				transaction_id: transactionId,
			});
		}

		return false;
	}
}

export function displayPaymentErrorMessage(error?: string): void {
	$qsAll('.pp-pm-error-text', $el => {
		$el.remove();
	});

	if (!error) {
		return;
	}

	const gatewayId = PaymentConfiguration.selectedGateway();
	const $container = $qsAll(`div.pp-pm-saved-option[data-gateway="${gatewayId}"]`);

	$container.forEach($el => {
		$el.insertAdjacentHTML('beforebegin', /* html */ `<div class="pp-pm-error-text"><span>${error}</span></div>`);
	});
}
